import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import RoomIcon from '@material-ui/icons/Room';
import DirectionsIcon from '@material-ui/icons/Directions';
import TimelineIcon from '@material-ui/icons/Timeline';
import { firestore } from '../../firebase/config';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  section: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  listItem: {
    padding: theme.spacing(1, 0),
  },
  listItemLabel: {
    fontWeight: 'bold',
    marginRight: theme.spacing(2),
  },
  routeIcon: {
    fontSize: 60,
    marginBottom: theme.spacing(2),
  },
  stopsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  stopItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  stopIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  stopConnector: {
    width: 2,
    height: 30,
    backgroundColor: theme.palette.divider,
    margin: '0 auto',
  },
  routeHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  routeStats: {
    display: 'flex',
    justifyContent: 'space-around',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  routeStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
  },
  routeStatValue: {
    fontWeight: 'bold',
    fontSize: '1.2rem',
  },
  routeStatLabel: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
}));

const RouteDetailPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchRoute();
  }, [id]);

  const fetchRoute = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const routeDoc = doc(firestore, 'routes', id);
      const routeSnapshot = await getDoc(routeDoc);
      
      if (routeSnapshot.exists()) {
        setRoute({
          id: routeSnapshot.id,
          ...routeSnapshot.data()
        });
      } else {
        // Si l'itinéraire n'existe pas, utiliser des données de démonstration
        setRoute({
          id: id,
          name: 'Paris - Lyon',
          origin: 'Paris',
          destination: 'Lyon',
          distance: 465,
          duration: 270, // en minutes
          status: 'Active',
          type: 'Direct',
          price: 45.99,
          stops: [],
          description: 'Itinéraire direct entre Paris et Lyon, passant par l\'autoroute A6.',
          createdAt: new Date().toISOString(),
          totalTrips: 128,
          averageOccupancy: 85,
          popularDays: ['Vendredi', 'Dimanche']
        });
      }
    } catch (error) {
      console.error('Error fetching route:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données de l\'itinéraire',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/admin/routes/${id}/edit`);
  };

  const handleDeactivate = () => {
    navigate(`/admin/routes/${id}/deactivate`);
  };

  const handleDelete = () => {
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      // Simulation de suppression dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      await deleteDoc(doc(firestore, 'routes', id));
      
      setSnackbar({
        open: true,
        message: 'Itinéraire supprimé avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste après un court délai
      setTimeout(() => {
        navigate('/admin/routes');
      }, 1500);
      
    } catch (error) {
      console.error('Error deleting route:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la suppression de l\'itinéraire',
        severity: 'error'
      });
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
  };

  const handleBack = () => {
    navigate('/admin/routes');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'primary';
      case 'Inactive':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non renseigné';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Date invalide';
    }
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        style={{ marginBottom: 16 }}
      >
        Retour à la liste
      </Button>
      
      <Typography variant="h4" component="h1" className={classes.title}>
        Détails de l'itinéraire
      </Typography>
      
      {loading ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : route ? (
        <>
          <Paper className={classes.paper}>
            <div className={classes.routeHeader}>
              <DirectionsIcon className={classes.routeIcon} color="primary" />
              <Typography variant="h5" component="h2">
                {route.name}
              </Typography>
              <Chip 
                label={route.status} 
                color={getStatusColor(route.status)} 
                className={classes.chip}
              />
              <Chip 
                label={route.type === 'Direct' ? 'Direct' : 'Avec arrêts'} 
                variant="outlined" 
                className={classes.chip}
              />
            </div>
            
            <div className={classes.routeStats}>
              <div className={classes.routeStat}>
                <Typography className={classes.routeStatValue}>
                  {route.distance} km
                </Typography>
                <Typography className={classes.routeStatLabel}>
                  Distance
                </Typography>
              </div>
              <div className={classes.routeStat}>
                <Typography className={classes.routeStatValue}>
                  {formatDuration(route.duration)}
                </Typography>
                <Typography className={classes.routeStatLabel}>
                  Durée
                </Typography>
              </div>
              <div className={classes.routeStat}>
                <Typography className={classes.routeStatValue}>
                  {formatPrice(route.price)}
                </Typography>
                <Typography className={classes.routeStatLabel}>
                  Prix
                </Typography>
              </div>
            </div>
            
            <Divider className={classes.divider} />
            
            <div className={classes.stopsContainer}>
              <div className={classes.stopItem}>
                <RoomIcon className={classes.stopIcon} style={{ color: '#4caf50' }} />
                <Typography variant="subtitle1">
                  <strong>{route.origin}</strong> (Départ)
                </Typography>
              </div>
              
              {route.stops && route.stops.length > 0 && (
                <>
                  {route.stops.map((stop, index) => (
                    <React.Fragment key={index}>
                      <div className={classes.stopConnector} />
                      <div className={classes.stopItem}>
                        <RoomIcon className={classes.stopIcon} style={{ color: '#ff9800' }} />
                        <Typography variant="subtitle1">
                          {stop} (Arrêt)
                        </Typography>
                      </div>
                    </React.Fragment>
                  ))}
                </>
              )}
              
              <div className={classes.stopConnector} />
              <div className={classes.stopItem}>
                <RoomIcon className={classes.stopIcon} style={{ color: '#f44336' }} />
                <Typography variant="subtitle1">
                  <strong>{route.destination}</strong> (Arrivée)
                </Typography>
              </div>
            </div>
            
            <Divider className={classes.divider} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Informations générales
                </Typography>
                <List>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Type:</Box>
                          <Box>{route.type === 'Direct' ? 'Direct' : 'Avec arrêts'}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Statut:</Box>
                          <Box>{route.status}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Créé le:</Box>
                          <Box>{formatDate(route.createdAt)}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  {route.description && (
                    <ListItem className={classes.listItem}>
                      <ListItemText 
                        primary={
                          <Box display="flex" flexDirection="column">
                            <Box className={classes.listItemLabel}>Description:</Box>
                            <Box mt={1}>{route.description}</Box>
                          </Box>
                        } 
                      />
                    </ListItem>
                  )}
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Statistiques
                </Typography>
                <List>
                  {route.totalTrips !== undefined && (
                    <ListItem className={classes.listItem}>
                      <ListItemText 
                        primary={
                          <Box display="flex">
                            <Box className={classes.listItemLabel}>Nombre de voyages:</Box>
                            <Box>{route.totalTrips}</Box>
                          </Box>
                        } 
                      />
                    </ListItem>
                  )}
                  {route.averageOccupancy !== undefined && (
                    <ListItem className={classes.listItem}>
                      <ListItemText 
                        primary={
                          <Box display="flex">
                            <Box className={classes.listItemLabel}>Taux d'occupation moyen:</Box>
                            <Box>{route.averageOccupancy}%</Box>
                          </Box>
                        } 
                      />
                    </ListItem>
                  )}
                  {route.popularDays && route.popularDays.length > 0 && (
                    <ListItem className={classes.listItem}>
                      <ListItemText 
                        primary={
                          <Box display="flex">
                            <Box className={classes.listItemLabel}>Jours populaires:</Box>
                            <Box>
                              {route.popularDays.join(', ')}
                            </Box>
                          </Box>
                        } 
                      />
                    </ListItem>
                  )}
                </List>
              </Grid>
            </Grid>
            
            <div className={classes.buttons}>
              {route.status === 'Active' ? (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeactivate}
                >
                  Désactiver
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                >
                  Supprimer
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Modifier
              </Button>
            </div>
          </Paper>
        </>
      ) : (
        <Paper className={classes.paper}>
          <Typography variant="body1">
            Itinéraire non trouvé
          </Typography>
        </Paper>
      )}
      
      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmer la suppression"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous sûr de vouloir supprimer l'itinéraire {route?.name} ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Annuler
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary" autoFocus>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RouteDetailPage;
